import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError, money, dt, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea, statusTone } from "../ui";

export default function RhmCustomers() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [openId, setOpenId] = useState(null);
    const [notes, setNotes] = useState("");
    const [buyersOnly, setBuyersOnly] = useState(false);
    const [sort, setSort] = useState("recent");

    const { data: customers = [], isLoading } = useQuery({
        queryKey: ["rhm-customers", q, buyersOnly, sort],
        queryFn: async () => (await rhm.get("/customers", {
            params: { q: q || undefined, buyers_only: buyersOnly || undefined, sort },
        })).data,
    });
    const { data: detail } = useQuery({
        queryKey: ["rhm-customer", openId],
        queryFn: async () => (await rhm.get(`/customers/${openId}`)).data,
        enabled: !!openId,
    });

    const save = useMutation({
        mutationFn: async ({ id, payload }) => (await rhm.patch(`/customers/${id}`, payload)).data,
        onSuccess: () => {
            toast.success("Customer updated");
            qc.invalidateQueries({ queryKey: ["rhm-customer", openId] });
            qc.invalidateQueries({ queryKey: ["rhm-customers"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-customers-page">
            <PageHead title="Customers" sub="Every buyer, guest or registered, with a full 360° history." />

            <Card title={`${customers.length} ${buyersOnly ? "buyer" : "customer"}(s)`}
                action={
                    <div className="flex items-center gap-2">
                        <button onClick={() => setBuyersOnly(!buyersOnly)} data-testid="customers-buyers-toggle"
                            className={`mono text-[10px] tracking-[0.14em] px-3 py-2 border transition-colors ${
                                buyersOnly ? "border-lime text-lime bg-lime/10" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                            {buyersOnly ? "BUYERS ONLY" : "ALL CUSTOMERS"}
                        </button>
                        <Select className="!w-44" value={sort} data-testid="customers-sort"
                            onChange={(e) => setSort(e.target.value)}
                            options={[{ value: "recent", label: "Newest first" },
                                { value: "oldest", label: "Oldest first" },
                                { value: "spent", label: "Highest spend" },
                                { value: "spent_low", label: "Lowest spend" },
                                { value: "orders", label: "Most orders" }]} />
                        <Input placeholder="Search name / email / phone" value={q} onChange={(e) => setQ(e.target.value)}
                            className="!w-56" data-testid="customer-search" />
                    </div>
                }>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-customers-table"
                        head={["Name", "Email", "Phone", "Orders", "Spent", "Source", "Account", "Joined", ""]}
                        empty={!customers.length && <Empty msg="No customers yet." />}>
                        {customers.map((c) => (
                            <tr key={c.id} data-testid={`rhm-customer-row-${c.email}`}>
                                <Td className="font-semibold">{c.name || "—"}</Td>
                                <Td className="text-sm text-zinc-400">{c.email}</Td>
                                <Td className="text-sm">{c.phone || "—"}</Td>
                                <Td>{c.orders_count ?? c.total_orders ?? 0}</Td>
                                <Td>{money(c.total_spent || 0)}</Td>
                                <Td className="text-sm text-zinc-400">{titleCase(c.source || "")}</Td>
                                <Td><Badge tone={c.auth_provider === "guest" ? "muted" : "gold"}>{(c.auth_provider || "guest").toUpperCase()}</Badge></Td>
                                <Td className="text-xs text-zinc-500">{dOnly(c.created_at)}</Td>
                                <Td>
                                    <button onClick={() => { setOpenId(c.id); setNotes(c.notes || ""); }}
                                        data-testid={`rhm-open-customer-${c.email}`}
                                        className="mono text-[10px] tracking-[0.16em] text-gold">OPEN</button>
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!openId} onClose={() => setOpenId(null)} wide testId="rhm-customer-detail"
                title={detail ? `${detail.name || detail.email}` : "Customer"}>
                {!detail ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-4 gap-4">
                            {[["Orders", detail.orders?.length || 0], ["Lifetime value", money(detail.lifetime_value)],
                              ["Offers used", detail.offers_used?.length || 0], ["Reorders", detail.reorders?.length || 0]].map(([l, v]) => (
                                <div key={l} className="border border-white/10 p-4">
                                    <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">{l.toUpperCase()}</div>
                                    <div className="font-display text-2xl mt-1">{v}</div>
                                </div>
                            ))}
                        </div>

                        <div className="grid md:grid-cols-2 gap-5 text-sm">
                            <div className="border border-white/10 p-4">
                                <div className="mono text-[10px] tracking-[0.18em] text-gold mb-3">CONTACT</div>
                                <div className="text-zinc-300 space-y-1">
                                    <div>{detail.email}</div>
                                    <div>{detail.phone || "No phone"}</div>
                                    <div className="text-zinc-500">Source: {titleCase(detail.source || "website")}</div>
                                    <div className="text-zinc-500">Joined {dt(detail.created_at)}</div>
                                </div>
                            </div>
                            <div className="border border-white/10 p-4">
                                <div className="mono text-[10px] tracking-[0.18em] text-gold mb-3">ADDRESS ON FILE</div>
                                {detail.addresses?.length ? (
                                    <div className="text-zinc-300">
                                        {detail.addresses[0].address_line1}, {detail.addresses[0].city}, {detail.addresses[0].state} {detail.addresses[0].pincode}
                                    </div>
                                ) : <div className="text-zinc-500">No address saved.</div>}
                            </div>
                        </div>

                        <div>
                            <div className="mono text-[10px] tracking-[0.18em] text-gold mb-3">ORDERS</div>
                            <Table head={["Order", "Items", "Total", "Coupon", "Status", "Placed"]}
                                empty={!detail.orders?.length && <Empty msg="No orders yet." />}>
                                {(detail.orders || []).map((o) => (
                                    <tr key={o.order_number}>
                                        <Td className="mono text-xs text-gold">{o.order_number}</Td>
                                        <Td className="text-sm text-zinc-400">{o.items?.map((i) => `${i.product_name} × ${i.quantity}`).join(", ")}</Td>
                                        <Td>{money(o.total)}</Td>
                                        <Td className="mono text-[10px]">{o.coupon_code || "—"}</Td>
                                        <Td><Badge tone={statusTone(o.status)}>{titleCase(o.status)}</Badge></Td>
                                        <Td className="text-xs text-zinc-500">{dt(o.created_at)}</Td>
                                    </tr>
                                ))}
                            </Table>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <div className="mono text-[10px] tracking-[0.18em] text-gold mb-3">PRODUCTS PURCHASED</div>
                                {detail.products_purchased?.length ? detail.products_purchased.map((p) => (
                                    <div key={p.product_name} className="flex justify-between border-b border-white/5 py-2 text-sm">
                                        <span>{p.product_name}</span>
                                        <span className="text-zinc-400">{p.units} units · {money(p.value)}</span>
                                    </div>
                                )) : <Empty msg="Nothing purchased yet." />}
                            </div>
                            <div>
                                <div className="mono text-[10px] tracking-[0.18em] text-gold mb-3">EXPECTED REORDERS</div>
                                {detail.reorders?.length ? detail.reorders.map((r) => (
                                    <div key={r.id} className="flex justify-between border-b border-white/5 py-2 text-sm">
                                        <span>{r.product_name}</span>
                                        <span className={r.state === "overdue" ? "text-red-400" : "text-zinc-400"}>
                                            {r.expected_date} · {String(r.state).toUpperCase()}
                                        </span>
                                    </div>
                                )) : <Empty msg="No reorder prediction yet." />}
                            </div>
                        </div>

                        {can("customers.edit") && (
                            <div>
                                <Field label="Internal notes">
                                    <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="customer-notes" />
                                </Field>
                                <Btn className="mt-3" data-testid="customer-save"
                                    onClick={() => save.mutate({ id: detail.id, payload: { notes } })}>SAVE NOTES</Btn>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
